<script setup lang="ts">
import './assets/style.scss'
import { useConfirm, VIcons } from '@/shared/ui'
import { onBeforeUnmount, onMounted, type PropType, ref, watch } from 'vue'
import { useTranslate } from '@/shared/lib/i18n'
import type {
  GridFieldDragType,
  GridFieldType,
} from '@/features/Inventory/ObjectTypePassportGrid/model/types.ts'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { InventTypePassportGridField } from '@/entities/Inventory/TypePassportGridField'
import InventObjectTypePassportGridItems from './InventObjectTypePassportGridItems.vue'
import { map, max } from 'lodash'

const props = defineProps({
  fields: {
    type: Array as PropType<ObjectInventoryField[]>,
    default: () => [],
  },
})

const gridFields = defineModel<GridFieldType[]>({ default: [] })

const { trs } = useTranslate()
const { show } = useConfirm()
const rowCells = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
const defaultLen = ref(4)

const searchQuery = ref('')

const gridTemplate = ref<number[][]>([])

/**
 * Вычисляем максимальную высоту сетки
 */
watch(
  gridFields,
  () => {
    const counter = (max(map(gridFields.value, 'row')) || 3) + 1

    for (let i = 0; i < counter; i++) {
      gridTemplate.value.push(rowCells)
    }
  },
  { immediate: true, once: true },
)

/**
 * Функция добавления строки
 */
const addRowHandler = () => {
  gridTemplate.value.push(rowCells)
}

/**
 * Функция удаления строки
 * @param index
 */
const removeRowHandler = (index: number) => {
  show({
    title: 'Удалить строку?',
    confirmFunc: () => {
      gridTemplate.value.splice(index, 1)
      gridFields.value = gridFields.value.filter((item) => item.row !== index)
      gridFields.value.map((item) => {
        if (item.row > index) item.row--
      })
    },
  })
}

const selectedCells = ref<number[][]>([])
const cellElem = ref<null | HTMLElement>(null)
const dragField = ref<null | GridFieldDragType>(null)
const editField = ref<null | string>(null)

/**
 * Функция обработки окончания перетаскивания
 * @param row
 * @param cell
 */
const dropHandler = (row: number, cell: number) => {
  if (row !== undefined && cell !== undefined && dragField.value) {
    const base = { ...dragField.value, row, cell, length: defaultLen.value }
    const item =
      dragField.value.type === 'heading'
        ? {
            ...base,
            id: crypto.randomUUID(),
            type: 'heading' as const,
            name: trs('itf.heading_default'),
          }
        : base
    gridFields.value = [...gridFields.value, item]
  }

  if (row !== undefined && cell !== undefined && editField.value) {
    const item = gridFields.value.find((item) => item.id === editField.value)

    if (item) {
      item.row = row
      item.cell = cell
    }
  }

  dragField.value = null
  selectedCells.value = []
}

/**
 * Функция снятия выделения с объекта
 */
const clickOutsideHandler = () => {
  editField.value = null
}

/**
 * Функция обработки перемещения при перетаскивании
 * @param row
 * @param cell
 */
const dragOverHandler = (row: number, cell: number) => {
  const cells: number[][] = []

  for (let i = 0; i < defaultLen.value; i++) {
    cells.push([row, cell + i])
  }

  selectedCells.value = cells
}

/**
 * Функция обработки выбора элемента перетаскивания
 * @param field
 */
const dragHandler = (field: GridFieldDragType) => {
  dragField.value = field
}

/**
 * Функция потери фокуса при перемещении
 */
const dragLeaveHandler = () => {
  selectedCells.value = []
}

/**
 * Определяем находится ли ячейка в выбранном диапазоне
 * @param row
 * @param cell
 */
const isSelectCell = (row: number, cell: number) => {
  return selectedCells.value.some(([r, c]) => r === row && c === cell)
}

/**
 * Функция удаления поля из сетки
 * @param event
 */
const editFieldRemove = (event: KeyboardEvent) => {
  event.preventDefault()

  if (!editField.value || event.key !== 'Backspace') return

  gridFields.value = gridFields.value.filter((item) => item.id !== editField.value)
  editField.value = null
}

/**
 * Состояние и обработчики для удаления поля через иконку корзины в конце строки
 */
const activeTrashRow = ref<number | null>(null)

const trashDragOverHandler = (row: number) => {
  if (!editField.value) return

  activeTrashRow.value = row
}

const trashDragLeaveHandler = () => {
  activeTrashRow.value = null
}

const trashDropHandler = (row: number) => {
  if (!editField.value) return

  activeTrashRow.value = null

  show({
    title: 'Удалить элемент?',
    confirmFunc: () => {
      gridFields.value = gridFields.value.filter((item) => item.id !== editField.value)
      editField.value = null
    },
  })
}

onMounted(() => {
  cellElem.value = document.querySelector('.invent-pass-grid__grid-cell')

  document.addEventListener('keyup', editFieldRemove)
})

onBeforeUnmount(() => {
  document.removeEventListener('keyup', editFieldRemove)
})
</script>

<template>
  <div class="invent-pass-grid__grid">
    <div class="invent-pass-grid__fields">
      <div
        class="invent-pass-grid-field__item invent-pass-grid-field__item_heading mb-4"
        draggable="true"
        @dragstart="
          dragHandler({
            type: 'heading',
            name: $t('itf.add_heading'),
            id: 'heading-template',
          })
        "
      >
        <div class="invent-pass-grid-field__main">
          <VTextField
            class="invent-pass-grid-field__field"
            :model-value="$t('itf.add_heading')"
            readonly
            hide-details
          />
        </div>
      </div>
      <div class="invent-pass-grid__search-row">
        <VTextField
          v-model="searchQuery"
          class="invent-pass-grid__search"
          :label="$t('itf.search')"
          hide-details
        />
        <VBtn
          class="invent-pass-grid__search-clear"
          variant="text"
          density="comfortable"
          :disabled="!searchQuery"
          @click="searchQuery = ''"
        >
          <VIcons type="close" />
        </VBtn>
      </div>
      <template v-for="field in fields" :key="field.id">
        <InventTypePassportGridField
          :field
          :search-query="searchQuery"
          @drag="dragHandler"
        />
      </template>
    </div>

    <div>
      <div class="invent-pass-grid__grid_sticky">
        <div
          class="invent-pass-grid__grid-area"
          @click="clickOutsideHandler"
          v-click-outside="clickOutsideHandler"
        >
          <div
            v-for="(cells, row) in gridTemplate"
            :key="cells.length + row"
            class="invent-pass-grid__grid-row"
          >
            <div
              v-for="cell in cells"
              :key="cell"
              class="invent-pass-grid__grid-cell"
              :class="{ 'invent-pass-grid__grid-cell_active': isSelectCell(row, cell) }"
              :data-cell="cell"
              :data-row="row"
              @drop="dropHandler(row, cell)"
              @dragover.prevent="dragOverHandler(row, cell)"
              @dragenter.prevent
              @dragleave.prevent="dragLeaveHandler"
            ></div>

            <VBtn
              variant="tonal"
              color="error"
              class="invent-pass-grid__row-trash"
              :class="{ 'invent-pass-grid__row-trash_active': activeTrashRow === row }"
              @click="removeRowHandler(row)"
              @dragover.prevent="trashDragOverHandler(row)"
              @dragenter.prevent="trashDragOverHandler(row)"
              @dragleave.prevent="trashDragLeaveHandler"
              @drop.prevent="trashDropHandler(row)"
            >
              <VIcons type="delete" />
            </VBtn>
          </div>

          <InventObjectTypePassportGridItems
            :cell-elem="cellElem"
            v-model:gridFields="gridFields"
            v-model:editField="editField"
            v-model:defaultLen="defaultLen"
          />
        </div>

        <VBtn variant="tonal" class="w-full mt-4" @click="addRowHandler">
          <VIcons type="add_circle" /> {{ $t('itf.add_row') }}
        </VBtn>
      </div>
    </div>
  </div>
</template>
